import { apiClient } from '@/core/api/client';
import type { 
  User, 
  UserFilters, 
  CreateUserDto, 
  UpdateUserDto,
  ApiResponse,
  FMExpert,
  FMCustomer
} from '../types';

export const userManagementAPI = {
  // Get all users with filters
  async getUsers(filters?: UserFilters): Promise<ApiResponse<User[]>> {
    try {
      const params = new URLSearchParams();
      if (filters?.role && filters.role !== 'all') {
        params.append('role', filters.role);
      }
      if (filters?.status && filters.status !== 'all') {
        if (filters.status === 'active') {
          params.append('isActive', 'true');
          params.append('isFrozen', 'false');
        } else if (filters.status === 'frozen') {
          params.append('isActive', 'true');
          params.append('isFrozen', 'true');
        }
      }
      if (filters?.search) {
        params.append('search', filters.search);
      }

      return apiClient.get<ApiResponse<User[]>>(`/api/admin/users?${params}`);
    } catch (error) {
      throw new Error('Failed to fetch users');
    }
  },

  // Create new user
  async createUser(data: CreateUserDto): Promise<ApiResponse<User>> {
    try {
      return apiClient.post<ApiResponse<User>>('/api/admin/users', data);
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data;
      }
      throw new Error('Failed to create user');
    }
  },

  // Update existing user
  async updateUser(userId: string, data: UpdateUserDto): Promise<ApiResponse<User>> {
    try {
      return apiClient.put<ApiResponse<User>>(`/api/admin/users/${userId}`, {
        ...data,
        isActive: data.isActive !== false
      });
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data;
      }
      throw new Error('Failed to update user');
    }
  },

  // Delete user
  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    try {
      return apiClient.delete<ApiResponse<void>>(`/api/admin/users/${userId}`);
    } catch (error) {
      throw new Error('Failed to delete user');
    }
  },

  // Freeze user account
  async freezeUser(userId: string): Promise<ApiResponse<void>> {
    try {
      return apiClient.post<ApiResponse<void>>(`/api/admin/users/${userId}/freeze`);
    } catch (error) {
      throw new Error('Failed to freeze user');
    }
  },

  // Unfreeze user account
  async unfreezeUser(userId: string): Promise<ApiResponse<void>> {
    try {
      return apiClient.post<ApiResponse<void>>(`/api/admin/users/${userId}/unfreeze`);
    } catch (error) {
      throw new Error('Failed to unfreeze user');
    }
  },

  // Reset user password
  async resetUserPassword(userId: string, newPassword: string): Promise<ApiResponse<void>> {
    try {
      return apiClient.post<ApiResponse<void>>(`/api/admin/users/${userId}/reset-password`, {
        newPassword
      });
    } catch (error) {
      throw new Error('Failed to reset password');
    }
  }
};

// FileMaker API integration
export const fileMakerAPI = {
  // Get list of experts from FileMaker
  async getExpertsList(): Promise<ApiResponse<FMExpert[]>> {
    try {
      return apiClient.get<ApiResponse<FMExpert[]>>('/api/fm/experts');
    } catch (error) {
      console.error('Failed to fetch experts:', error);
      return { success: false, data: [] };
    }
  },

  // Search FileMaker customers
  async searchCustomers(search: string): Promise<ApiResponse<FMCustomer[]>> {
    try {
      return apiClient.get<ApiResponse<FMCustomer[]>>(
        `/api/fm/customers/search?search=${encodeURIComponent(search)}`
      );
    } catch (error) {
      console.error('Failed to search customers:', error);
      return { success: false, data: [] };
    }
  }
};